// src/features/inventory/pages/ItemDetailPage/StockStatusCard.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Card, CardContent, Tooltip, Typography } from '@mui/material';
import { Add as AddIcon, CompareArrows as TransferIcon, Remove as RemoveIcon } from '@mui/icons-material';
import RecordTransactionDialog from './RecordTransactionDialog';
import TransferStockDialog from './TransferStockDialog';
import AddBatchDialog from './tabs/BatchNumbers/AddBatchDialog';
import { useInventoryLotPermissions } from '../../../auth/hooks/usePermissions';
import InfoTip from '../../../../components/common/InfoTip';
import { stockAPI } from '../../services/stock';
import { useApi } from '../../../../hooks/api/useApi';

export default function StockStatusCard({ item, itemId, canUpdate, onTransactionRecorded }) {
  const { t } = useTranslation(['inventory', 'common']);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [addBatchOpen, setAddBatchOpen] = useState(false);
  const { canCreate: canCreateLot } = useInventoryLotPermissions();
  const [locationBalances, setLocationBalances] = useState([]);
  const { execute } = useApi();

  const tx = item.last_stock_transaction;
  const currentStock = tx?.quantity_after ?? 0;
  const lotTracked = item.product_group?.lot_tracked ?? false;
  const refreshKey = tx?.stock_transaction_id;

  const fetchLocationBalances = useCallback(async () => {
    const result = await execute(() => stockAPI.getStockLevelByLocation(itemId));
    if (result.success) {
      const balances = result.data.data?.location_balances ?? [];
      // Keep negative places too (stock taken out without a location), so the split adds up
      setLocationBalances(balances.filter(b => b.quantity !== 0));
    }
  }, [execute, itemId]);

  // Refetch whenever stock is booked anywhere on the page
  useEffect(() => {
    fetchLocationBalances();
  }, [fetchLocationBalances, refreshKey]);

  const handleTransactionRecorded = () => onTransactionRecorded();

  const newTransactionButton = (
    <Button
      variant="contained"
      startIcon={<AddIcon />}
      onClick={() => setDialogOpen(true)}
      size="medium"
      sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
    >
      {t('inventory:items.stock_status.new_transaction')}
    </Button>
  );

  // A lot-tracked item gains stock only as a new batch and loses it oldest batch first,
  // so it gets one button for each instead of a "new transaction" that can only remove
  const lotButtons = (
    <>
      <Tooltip title={t('inventory:items.stock_status.remove_fifo_hint')} describeChild>
        <Button
          variant={canCreateLot ? 'outlined' : 'contained'}
          startIcon={<RemoveIcon />}
          onClick={() => setDialogOpen(true)}
          size="medium"
          sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          {t('inventory:items.stock_status.remove_stock')}
        </Button>
      </Tooltip>
      {canCreateLot && (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddBatchOpen(true)}
          size="medium"
          sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          {t('inventory:items.tabs.batches.dialog.open_button')}
        </Button>
      )}
    </>
  );

  const transferButton = (
    <Button
      variant="outlined"
      startIcon={<TransferIcon />}
      onClick={() => setTransferOpen(true)}
      size="medium"
      sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
    >
      {t('inventory:items.transfer_stock.button')}
    </Button>
  );

  return (
    <>
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
        <CardContent sx={{ px: 7, py: '20px !important' }}>

          {/* Big stock number */}
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 3.5 }}>
            <Typography variant="h1" fontWeight="bold" lineHeight={1}>
              {currentStock}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {item.uom} {t('inventory:items.stock_status.in_stock')}
            </Typography>
            <InfoTip title={t('inventory:help.stock.on_hand')} sx={{ alignSelf: 'center' }} />
          </Box>

          {/* Row 2: button */}
          <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
            {/* Location breakdown table */}
            {locationBalances.length > 0 && (
              <Box sx={{ mb: 2, display: 'grid', gridTemplateColumns: `repeat(${locationBalances.length}, auto) 1fr`, columnGap: 3 }}>
                {/* Header row: location names */}
                {locationBalances.map((b) => (
                  <Box key={`name-${b.location_id}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" color="text.disabled" fontWeight={500} sx={{ letterSpacing: '0.04em' }}>
                      {b.location_id == null ? t('inventory:items.no_location') : b.location_name}
                    </Typography>
                    <InfoTip
                      size={14}
                      title={t(b.location_id == null ? 'inventory:help.stock.no_location' : 'inventory:help.stock.location')}
                    />
                  </Box>
                ))}
                <span />
                {/* Value row: quantities */}
                {locationBalances.map((b) => (
                  <Typography key={`qty-${b.location_id}`} variant="body2" color={b.quantity < 0 ? 'error.main' : undefined}>
                    <b>{b.quantity}</b> {item.uom}
                  </Typography>
                ))}
                <span />
              </Box>
            )}

            {canUpdate && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                {locationBalances.some(b => b.quantity > 0) && transferButton}
                {lotTracked ? lotButtons : newTransactionButton}
              </Box>
            )}
          </Box>

        </CardContent>
      </Card>

      <RecordTransactionDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSuccess={handleTransactionRecorded}
        itemId={itemId}
        currentStock={currentStock}
        uom={item.uom}
        lotTracked={lotTracked}
      />

      {lotTracked && canCreateLot && (
        <AddBatchDialog
          open={addBatchOpen}
          onClose={() => setAddBatchOpen(false)}
          onSuccess={handleTransactionRecorded}
          itemId={itemId}
          uom={item.uom}
        />
      )}

      <TransferStockDialog
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
        onSuccess={handleTransactionRecorded}
        itemId={itemId}
        uom={item.uom}
        lotTracked={lotTracked}
        locationBalances={locationBalances}
      />
    </>
  );
}
